import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import apiService from './services/api';
import { Panel, Button, LoadingSpinner, CreateAppointmentModal } from './components';
import { CalendarEventFull } from './components/CalendarEvent';
import './calendar.css';

// date-fns localizer setup
const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// Technician color palette (12 distinct colors)
const TECH_COLORS = [
  '#DC2626', // red
  '#2563EB', // blue
  '#059669', // emerald
  '#D97706', // amber
  '#7C3AED', // violet
  '#DB2777', // pink
  '#0891B2', // cyan
  '#65A30D', // lime
  '#EA580C', // orange
  '#4F46E5', // indigo
  '#0D9488', // teal
  '#CA8A04', // yellow
];

const getTechnicianColor = (technicianId, techniciansList) => {
  const index = techniciansList.findIndex(t => t.id === technicianId);
  if (index === -1) return '#9CA3AF'; // gray fallback
  return TECH_COLORS[index % TECH_COLORS.length];
};

// Format date as YYYY-MM-DD in local timezone
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get ISO week number
const getISOWeek = (date) => {
  const target = new Date(date.valueOf());
  const dayNum = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNum + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  return 1 + Math.round(((target - firstThursday) / 86400000) / 7);
};

function CalendarPage() {
  const navigate = useNavigate();

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('week');

  // Data state
  const [appointments, setAppointments] = useState([]);
  const [daysOff, setDaysOff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPrefillDate, setCreatePrefillDate] = useState(null);
  const [createPrefillTime, setCreatePrefillTime] = useState(null);

  // Filter state
  const [searchParams] = useSearchParams();
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState(searchParams.get('technician') || '');
  const [typeFilter, setTypeFilter] = useState('all');

  // Availability management state
  const [showAvailability, setShowAvailability] = useState(false);
  const [availTech, setAvailTech] = useState('');
  const [availDateFrom, setAvailDateFrom] = useState(formatDateLocal(new Date()));
  const [availDateTo, setAvailDateTo] = useState(formatDateLocal(new Date()));
  const [availBlockType, setAvailBlockType] = useState('all_day');
  const [availStartTime, setAvailStartTime] = useState('09:00');
  const [availEndTime, setAvailEndTime] = useState('17:00');
  const [availNote, setAvailNote] = useState('');
  const [availSubmitting, setAvailSubmitting] = useState(false);

  // Fetch locations and technicians on mount
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [locData, techData] = await Promise.all([
          apiService.getLocations(),
          apiService.getStaff({ is_technician: true }),
        ]);
        setLocations(Array.isArray(locData) ? locData : locData.results || []);
        const allTechs = Array.isArray(techData) ? techData : techData.results || [];
        setTechnicians(allTechs.filter(t => !t.internal_status || t.internal_status === 'active'));
      } catch (err) {
        console.error('Error fetching reference data:', err);
      }
    };
    fetchReferenceData();
  }, []);

  // Compute the visible date range based on current view + date
  const visibleRange = useMemo(() => {
    const d = currentDate;
    if (currentView === 'day') {
      return { start: new Date(d), end: new Date(d) };
    }
    // week
    const weekStart = startOfWeek(d, { weekStartsOn: 0 });
    return { start: weekStart, end: addDays(weekStart, 6) };
  }, [currentDate, currentView]);

  // Fetch calendar data when view/date/filters change
  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build appointment params based on view
      const params = { ordering: 'date_time' };
      if (currentView === 'day') {
        params.date_time__date = formatDateLocal(currentDate);
      } else {
        // Use date range instead of ISO week number to avoid
        // Sunday boundary mismatch (US weeks start Sunday, ISO Monday)
        params.date_time__date__gte = formatDateLocal(visibleRange.start);
        params.date_time__date__lte = formatDateLocal(visibleRange.end);
      }

      if (selectedLocation) params.technician__location = selectedLocation;
      if (selectedTechnician) params.technician = selectedTechnician;
      if (typeFilter === 'alf') params.is_alf = true;
      if (typeFilter === 'event') params.is_event = true;

      // Days off params
      const daysOffParams = {
        date_from: formatDateLocal(visibleRange.start),
        date_to: formatDateLocal(visibleRange.end),
      };
      if (selectedTechnician) daysOffParams.technician = selectedTechnician;

      // Fetch all appointment pages + days off in parallel
      const fetchAllPages = async (baseParams) => {
        let all = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const data = await apiService.getAppointments({ ...baseParams, page });
          const results = data.results || data || [];
          all = [...all, ...results];
          hasMore = !!data.next;
          page++;
          if (page > 20) break;
        }
        return all;
      };

      const [apptData, offData] = await Promise.all([
        fetchAllPages(params),
        apiService.getTechnicianDaysOff(daysOffParams),
      ]);

      setAppointments(apptData);
      setDaysOff(Array.isArray(offData) ? offData : offData.results || []);
    } catch (err) {
      setError(err.message || 'Failed to load calendar data');
      console.error('Error fetching calendar data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentDate, currentView, selectedLocation, selectedTechnician, typeFilter, visibleRange]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Transform appointments into calendar events, grouping ALF visits
  const calendarEvents = useMemo(() => {
    const filtered = appointments.filter(appt => {
      const status = appt.status?.toUpperCase() || '';
      if (status.includes('CANCEL')) return false;
      if (typeFilter === 'standard' && appt.appointment_type !== 'Standard') return false;
      return true;
    });

    // Separate ALF facility visits from regular appointments
    // Use alf_facility (not is_alf) to avoid false positives from ALF-priced services on home visits
    const alfAppts = filtered.filter(a => a.alf_facility);
    const nonAlfAppts = filtered.filter(a => !a.alf_facility);

    // Group ALF appointments by facility (or client) + date + technician
    const alfGroups = new Map();
    alfAppts.forEach(appt => {
      const dateStr = appt.date_time ? appt.date_time.split('T')[0] : '';
      const key = appt.alf_facility
        ? `facility-${appt.alf_facility}-${dateStr}-${appt.technician}`
        : `${appt.client}-${dateStr}-${appt.technician}`;
      if (!alfGroups.has(key)) {
        alfGroups.set(key, []);
      }
      alfGroups.get(key).push(appt);
    });

    // Convert ALF groups into single calendar events
    const alfEvents = Array.from(alfGroups.values()).map(group => {
      const first = group[0];
      // Find earliest start time; use service durations if available, else 30min per resident (min 1hr)
      const times = group.map(a => new Date(a.date_time).getTime());
      const earliest = new Date(Math.min(...times));
      const totalServiceDuration = group.reduce((sum, a) => sum + (a.total_duration_minutes || 30), 0);
      const durationMs = Math.max(totalServiceDuration * 60 * 1000, 60 * 60 * 1000);
      const end = new Date(earliest.getTime() + durationMs);
      const techColor = getTechnicianColor(first.technician, technicians);
      const totalRevenue = group.reduce((sum, a) => sum + (parseFloat(a.expected_revenue) || 0), 0);

      const facilityName = first.alf_facility_name || first.client_name || 'ALF Facility';

      return {
        id: `alf-${first.alf_facility || first.client}-${earliest.getTime()}-${first.technician}`,
        title: `${facilityName} - ${group.length} residents`,
        start: earliest,
        end,
        techColor,
        isDayOff: false,
        isAlfGroup: true,
        alfGroup: {
          facilityId: first.alf_facility,
          clientId: first.client,
          clientName: facilityName,
          technicianId: first.technician,
          technicianName: first.technician_name,
          count: group.length,
          totalRevenue,
          date: earliest,
          appointmentIds: group.map(a => a.id),
        },
        resourceId: first.technician,
      };
    });

    // Convert non-ALF appointments into individual events
    const regularEvents = nonAlfAppts.map(appt => {
      const start = new Date(appt.date_time);
      const durationMs = (appt.total_duration_minutes || 60) * 60 * 1000;
      const end = new Date(start.getTime() + durationMs);
      const techColor = getTechnicianColor(appt.technician, technicians);
      return {
        id: appt.id,
        title: `${appt.client_name || 'Unknown'} - ${appt.technician_name || 'Unassigned'}${appt.city || appt.zip_code ? ` · ${appt.city || ''}${appt.city && appt.zip_code ? ' ' : ''}${appt.zip_code || ''}` : ''}${appt.address ? `\n${appt.address}` : ''}`,
        start,
        end,
        appointment: appt,
        techColor,
        isDayOff: false,
        isAlfGroup: false,
        resourceId: appt.technician,
      };
    });

    return [...regularEvents, ...alfEvents];
  }, [appointments, technicians, typeFilter]);

  // Transform days off into regular events with distinct styling
  const daysOffEvents = useMemo(() => {
    return daysOff.map(block => {
      const techColor = getTechnicianColor(block.technician, technicians);
      if (block.block_type === 'all_day') {
        return {
          id: `off-${block.id}`,
          title: `Day Off${block.note ? ': ' + block.note : ''}`,
          start: new Date(block.date + 'T09:00:00'),
          end: new Date(block.date + 'T18:00:00'),
          allDay: true,
          isDayOff: true,
          technicianId: block.technician,
          technicianName: block.technician_name,
          techColor,
          resourceId: block.technician,
        };
      }
      // Time range block
      return {
        id: `off-${block.id}`,
        title: `Unavailable${block.note ? ': ' + block.note : ''}`,
        start: new Date(block.date + 'T' + block.start_time),
        end: new Date(block.date + 'T' + block.end_time),
        allDay: false,
        isDayOff: true,
        technicianId: block.technician,
        technicianName: block.technician_name,
        techColor,
        resourceId: block.technician,
      };
    });
  }, [daysOff, technicians]);

  // Combined events: appointments + days off
  const allEvents = useMemo(() => {
    return [...calendarEvents, ...daysOffEvents];
  }, [calendarEvents, daysOffEvents]);

  // Filter technicians by selected location
  const filteredTechnicians = useMemo(() => {
    if (!selectedLocation) return technicians;
    return technicians.filter(t => String(t.location?.id) === String(selectedLocation));
  }, [technicians, selectedLocation]);

  // Build resources for week/day views (one column per technician)
  const resources = useMemo(() => {
    let relevantTechs;

    if (selectedTechnician) {
      // Single tech selected — show only them
      relevantTechs = filteredTechnicians.filter(t => String(t.id) === String(selectedTechnician));
    } else if (selectedLocation) {
      // Location filtered — show all techs in that location
      relevantTechs = filteredTechnicians;
    } else {
      // No filter — show only techs with data to avoid showing all 20+
      const techIdsInView = new Set([
        ...appointments.map(a => a.technician),
        ...daysOff.map(d => d.technician),
      ]);
      relevantTechs = filteredTechnicians.filter(t => techIdsInView.has(t.id));
      if (relevantTechs.length === 0) {
        relevantTechs = filteredTechnicians;
      }
    }

    return relevantTechs.map(t => ({
      resourceId: t.id,
      resourceTitle: `${t.person?.first_name || ''} ${t.person?.last_name?.charAt(0) || ''}.`,
    }));
  }, [technicians, appointments, daysOff, selectedTechnician, filteredTechnicians]);

  // Style events based on type (appointment vs day-off vs ALF group)
  const eventPropGetter = useCallback((event) => {
    if (event.isDayOff) {
      return {
        style: {
          backgroundColor: '#6b7280',
          border: '1px dashed #9ca3af',
          color: '#ffffff',
          borderRadius: '4px',
          opacity: 0.85,
        },
        className: 'calendar-dayoff-event',
      };
    }
    if (event.isAlfGroup) {
      return {
        style: {
          backgroundColor: '#faf5ff', // purple-50
          border: '1px solid #d8b4fe', // purple-300
          borderLeftWidth: '3px',
          borderLeftColor: event.techColor,
          color: '#111827',
          borderRadius: '4px',
        },
        className: 'calendar-alf-event',
      };
    }
    const appt = event.appointment;
    let bg = 'white';
    if (appt?.appointment_type === 'Event') bg = '#eff6ff'; // blue-50

    return {
      style: {
        backgroundColor: bg,
        border: '1px solid #e5e7eb',
        borderLeftWidth: '3px',
        borderLeftColor: event.techColor,
        color: '#111827',
        borderRadius: '4px',
      },
    };
  }, []);

  // Style day cells for days with all-day offs
  const dayPropGetter = useCallback((date) => {
    const dateStr = formatDateLocal(date);
    const hasFullDayOff = daysOff.some(
      d => d.date === dateStr && d.block_type === 'all_day'
    );
    if (hasFullDayOff) {
      return { style: { backgroundColor: '#d1d5db' } }; // gray-300
    }
    return {};
  }, [daysOff]);

  // Handle event click
  const handleEventClick = useCallback((event) => {
    if (event.isDayOff) {
      const blockId = parseInt(event.id.replace('off-', ''));
      const techName = event.technicianName || 'this technician';
      if (window.confirm(`Remove this block for ${techName}?`)) {
        handleDeleteBlock(blockId);
      }
      return;
    }
    if (event.isAlfGroup) {
      // Navigate to appointments page filtered by ALF facility (or client) and date
      const group = event.alfGroup;
      const dateStr = formatDateLocal(group.date);
      const filterParam = group.facilityId
        ? `alf_facility=${group.facilityId}`
        : `client=${group.clientId}`;
      navigate(`/appointments?${filterParam}&date=${dateStr}&is_alf=true`);
      return;
    }
    navigate(`/appointments/${event.id}`);
  }, [navigate]);

  // Handle navigation
  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  // Handle view change
  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  // Active technicians for legend (those in current view)
  const activeTechnicianIds = useMemo(() => {
    const ids = new Set([
      ...appointments.map(a => a.technician),
      ...daysOff.map(d => d.technician),
    ]);
    return [...ids];
  }, [appointments, daysOff]);

  const activeTechnicians = useMemo(() => {
    return technicians.filter(t => activeTechnicianIds.includes(t.id));
  }, [technicians, activeTechnicianIds]);

  // Navigate helpers for toolbar
  const goToToday = () => setCurrentDate(new Date());
  const goPrev = () => {
    const d = new Date(currentDate);
    if (currentView === 'week') d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setCurrentDate(d);
  };
  const goNext = () => {
    const d = new Date(currentDate);
    if (currentView === 'week') d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setCurrentDate(d);
  };

  // Format toolbar label
  const toolbarLabel = useMemo(() => {
    if (currentView === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
      const weekEnd = addDays(weekStart, 6);
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'd, yyyy')}`;
      }
      return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  }, [currentDate, currentView]);

  // Stats
  const stats = useMemo(() => {
    const total = calendarEvents.length;
    const confirmed = calendarEvents.filter(e =>
      ['accepted', 'confirmed'].includes(e.appointment?.status?.toLowerCase())
    ).length;
    const pending = calendarEvents.filter(e =>
      ['pending', 'scheduled'].includes(e.appointment?.status?.toLowerCase())
    ).length;
    const offDays = daysOff.filter(d => d.block_type === 'all_day').length;
    return { total, confirmed, pending, offDays };
  }, [calendarEvents, daysOff]);

  // Reset technician when location changes
  useEffect(() => {
    if (selectedLocation) {
      const techStillValid = filteredTechnicians.some(t => String(t.id) === String(selectedTechnician));
      if (!techStillValid) setSelectedTechnician('');
    }
  }, [selectedLocation, filteredTechnicians, selectedTechnician]);

  // Use resources (technician columns) in both week and day views
  const useResources = true;

  // ─── Availability Management ─────────────────────────────────
  // Generate array of date strings between from and to (inclusive)
  const getDateRange = (from, to) => {
    const dates = [];
    const current = new Date(from + 'T12:00:00');
    const end = new Date(to + 'T12:00:00');
    while (current <= end) {
      dates.push(formatDateLocal(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const availDayCount = useMemo(() => {
    if (!availDateFrom || !availDateTo || availDateFrom > availDateTo) return 0;
    return getDateRange(availDateFrom, availDateTo).length;
  }, [availDateFrom, availDateTo]);

  const handleCreateBlock = async () => {
    if (!availTech || !availDateFrom || !availDateTo || availDateFrom > availDateTo) return;
    setAvailSubmitting(true);
    try {
      const dates = getDateRange(availDateFrom, availDateTo);
      const isSingleDay = dates.length === 1;
      for (const date of dates) {
        if (availBlockType === 'all_day') {
          if (isSingleDay) {
            // Single day: toggle (create or remove)
            await apiService.toggleTechnicianDayOff({
              technician: parseInt(availTech),
              date,
              note: availNote,
            });
          } else {
            // Multi-day: always create, skip if already exists
            try {
              await apiService.createTechnicianDayOff({
                technician: parseInt(availTech),
                date,
                block_type: 'all_day',
                note: availNote,
              });
            } catch {
              // Already blocked — skip
            }
          }
        } else {
          await apiService.createTechnicianDayOff({
            technician: parseInt(availTech),
            date,
            block_type: 'time_range',
            start_time: availStartTime + ':00',
            end_time: availEndTime + ':00',
            note: availNote,
          });
        }
      }
      setAvailNote('');
      fetchCalendarData();
    } catch (err) {
      console.error('Failed to create block:', err);
    } finally {
      setAvailSubmitting(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await apiService.deleteTechnicianDayOff(blockId);
      fetchCalendarData();
    } catch (err) {
      console.error('Failed to delete block:', err);
    }
  };

  // Days off for the selected date range (for the management panel)
  const selectedDateBlocks = useMemo(() => {
    if (!availTech || !availDateFrom || !availDateTo) return [];
    return daysOff.filter(d =>
      String(d.technician) === String(availTech) && d.date >= availDateFrom && d.date <= availDateTo
    ).sort((a, b) => a.date.localeCompare(b.date));
  }, [daysOff, availTech, availDateFrom, availDateTo]);

  const HOUR_OPTIONS = useMemo(() =>
    Array.from({ length: 24 }, (_, i) => {
      const hour = i.toString().padStart(2, '0');
      const d = new Date(2026, 0, 1, i, 0);
      return { value: `${hour}:00`, label: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) };
    }), []
  );

  const handleSelectSlot = useCallback(({ start }) => {
    const d = new Date(start);
    const date = d.toLocaleDateString('en-CA');
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    navigate(`/new-booking?date=${date}&time=${time}`);
  }, [navigate]);

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {showCreateModal && (
          <CreateAppointmentModal
            onClose={() => { setShowCreateModal(false); setCreatePrefillDate(null); setCreatePrefillTime(null); }}
            onCreated={() => fetchCalendarData()}
            prefillDate={createPrefillDate}
            prefillTime={createPrefillTime}
          />
        )}
        {/* Page Header */}
        <div className="mb-3 flex justify-between items-center">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 leading-none">Calendar</h1>
            <p className="text-xs text-gray-500 mt-1">Appointments and team member availability</p>
          </div>
          <Button onClick={() => navigate('/new-booking')} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1" />
            New
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-gray-900">{stats.total}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Appointments</div>
          </Panel>
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-green-700">{stats.confirmed}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Confirmed</div>
          </Panel>
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-amber-600">{stats.pending}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Pending</div>
          </Panel>
          <Panel className="!p-2 text-center">
            <div className="text-lg font-bold text-red-600">{stats.offDays}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Days Off</div>
          </Panel>
        </div>

        {/* Filter Bar */}
        <Panel className="mb-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* Location */}
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>

            {/* Technician */}
            <select
              value={selectedTechnician}
              onChange={(e) => setSelectedTechnician(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
            >
              <option value="">All Team Members</option>
              {filteredTechnicians.map(tech => (
                <option key={tech.id} value={tech.id}>
                  {tech.person?.first_name} {tech.person?.last_name}
                </option>
              ))}
            </select>

            {/* Separator */}
            <div className="w-px h-5 bg-gray-200" />

            {/* Type Filters */}
            {['all', 'standard', 'alf', 'event'].map(type => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={`px-2 py-1 text-xs font-medium rounded border transition-colors ${
                  typeFilter === type
                    ? 'bg-red-100 text-red-800 border-red-300'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {type === 'all' ? 'All Types' : type === 'alf' ? 'ALF' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </Panel>

        {/* Availability Management Toggle */}
        <button
          onClick={() => setShowAvailability(!showAvailability)}
          className={`mb-3 w-full flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
            showAvailability
              ? 'bg-red-50 text-red-800 border-red-200'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
        >
          <span>Manage Availability / Block Slots</span>
          <svg className={`w-4 h-4 transition-transform ${showAvailability ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAvailability && (
          <Panel className="mb-3">
            <div className="space-y-3">
              <div className="flex flex-wrap items-end gap-2">
                {/* Technician */}
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Team Member</label>
                  <select
                    value={availTech}
                    onChange={(e) => setAvailTech(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  >
                    <option value="">Select...</option>
                    {technicians
                      .filter(t => t.internal_status === 'active')
                      .map(tech => (
                        <option key={tech.id} value={tech.id}>
                          {tech.person?.first_name} {tech.person?.last_name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Date From */}
                <div className="min-w-[120px]">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">From</label>
                  <input
                    type="date"
                    value={availDateFrom}
                    onChange={(e) => {
                      setAvailDateFrom(e.target.value);
                      if (e.target.value > availDateTo) setAvailDateTo(e.target.value);
                    }}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  />
                </div>

                {/* Date To */}
                <div className="min-w-[120px]">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">To</label>
                  <input
                    type="date"
                    value={availDateTo}
                    onChange={(e) => setAvailDateTo(e.target.value)}
                    min={availDateFrom}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  />
                </div>

                {/* Quick presets */}
                <div className="flex gap-1 self-end">
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const start = startOfWeek(today, { weekStartsOn: 1 });
                      const end = addDays(start, 6);
                      setAvailDateFrom(formatDateLocal(start));
                      setAvailDateTo(formatDateLocal(end));
                    }}
                    className="px-2 py-1.5 text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200 rounded hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    This Week
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const start = addDays(startOfWeek(today, { weekStartsOn: 1 }), 7);
                      const end = addDays(start, 6);
                      setAvailDateFrom(formatDateLocal(start));
                      setAvailDateTo(formatDateLocal(end));
                    }}
                    className="px-2 py-1.5 text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200 rounded hover:bg-gray-200 transition-colors whitespace-nowrap"
                  >
                    Next Week
                  </button>
                </div>

                {/* Block Type */}
                <div className="min-w-[120px]">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</label>
                  <select
                    value={availBlockType}
                    onChange={(e) => setAvailBlockType(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  >
                    <option value="all_day">All Day Off</option>
                    <option value="time_range">Time Range</option>
                  </select>
                </div>

                {/* Time Range (conditional) */}
                {availBlockType === 'time_range' && (
                  <>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">From</label>
                      <select
                        value={availStartTime}
                        onChange={(e) => setAvailStartTime(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                      >
                        {HOUR_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">To</label>
                      <select
                        value={availEndTime}
                        onChange={(e) => setAvailEndTime(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                      >
                        {HOUR_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>

              {/* Note + Submit */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Note (optional)</label>
                  <input
                    type="text"
                    value={availNote}
                    onChange={(e) => setAvailNote(e.target.value)}
                    placeholder="Reason for blocking"
                    maxLength={200}
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleCreateBlock}
                  disabled={!availTech || !availDateFrom || !availDateTo || availDateFrom > availDateTo || availSubmitting || (availBlockType === 'time_range' && availStartTime >= availEndTime)}
                >
                  {availSubmitting
                    ? `Blocking ${availDayCount} day${availDayCount !== 1 ? 's' : ''}...`
                    : availDayCount > 1
                      ? `Block ${availDayCount} Days`
                      : availBlockType === 'all_day' ? 'Toggle Day Off' : 'Add Block'
                  }
                </Button>
              </div>

              {availBlockType === 'time_range' && availStartTime >= availEndTime && (
                <p className="text-[10px] text-red-500">End time must be after start time</p>
              )}
              {availDateFrom > availDateTo && (
                <p className="text-[10px] text-red-500">End date must be on or after start date</p>
              )}

              {/* Existing blocks for selected tech + date range */}
              {availTech && selectedDateBlocks.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Existing blocks ({selectedDateBlocks.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedDateBlocks.map(block => (
                      <div key={block.id} className="flex items-center gap-1 bg-gray-100 border border-gray-200 rounded px-2 py-1">
                        <span className="text-xs text-gray-700">
                          <span className="font-medium">{block.date}</span>
                          {' — '}
                          {block.block_type === 'all_day'
                            ? 'All Day'
                            : `${block.start_time?.slice(0,5)} - ${block.end_time?.slice(0,5)}`
                          }
                          {block.note && ` (${block.note})`}
                        </span>
                        <button
                          onClick={() => handleDeleteBlock(block.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors ml-1"
                          title="Remove block"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Panel>
        )}

        {/* Custom Toolbar */}
        <Panel className="mb-3">
          <div className="flex items-center justify-between">
            {/* Navigation */}
            <div className="flex items-center gap-1">
              <Button variant="secondary" size="sm" onClick={goPrev}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" onClick={goToToday}>Today</Button>
              <Button variant="secondary" size="sm" onClick={goNext}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Label */}
            <h2 className="text-sm font-semibold text-gray-900">{toolbarLabel}</h2>

            {/* View Toggle */}
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              {['week', 'day'].map(view => (
                <button
                  key={view}
                  onClick={() => handleViewChange(view)}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    currentView === view
                      ? 'bg-red-800 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </Panel>

        {/* Calendar */}
        {error && (
          <Panel className="mb-3">
            <div className="text-red-600 text-xs">{error}</div>
          </Panel>
        )}

        <Panel noPadding className="overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          <BigCalendar
            localizer={localizer}
            events={allEvents}
            startAccessor="start"
            endAccessor="end"
            view={currentView}
            date={currentDate}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onSelectEvent={handleEventClick}
            components={{
              event: CalendarEventFull,
            }}
            eventPropGetter={eventPropGetter}
            dayPropGetter={dayPropGetter}
            style={{ minHeight: 700 }}
            popup
            selectable
            onSelectSlot={handleSelectSlot}
            toolbar={false}
            step={30}
            timeslots={2}
            min={new Date(2026, 0, 1, 9, 0, 0)}
            max={new Date(2026, 0, 1, 18, 0, 0)}
            {...(useResources && resources.length > 0 ? {
              resources,
              resourceIdAccessor: 'resourceId',
              resourceTitleAccessor: 'resourceTitle',
            } : {})}
          />
        </Panel>

        {/* Legend */}
        <Panel className="mt-3">
          <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
            {/* Technician Colors */}
            {activeTechnicians.length > 0 && (
              <div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Team Members</div>
                <div className="flex flex-wrap gap-2">
                  {activeTechnicians.map(tech => (
                    <div key={tech.id} className="flex items-center gap-1">
                      <span
                        className="inline-block w-3 h-3 rounded-sm border border-gray-200"
                        style={{ backgroundColor: getTechnicianColor(tech.id, technicians) }}
                      />
                      <span className="text-[10px] text-gray-600">
                        {tech.person?.first_name} {tech.person?.last_name?.charAt(0)}.
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status */}
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Confirmed', color: 'bg-green-500' },
                  { label: 'Pending', color: 'bg-amber-500' },
                  { label: 'Completed', color: 'bg-blue-500' },
                  { label: 'Cancelled', color: 'bg-red-500' },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-1">
                    <span className={`inline-block w-2 h-2 rounded-full ${s.color}`} />
                    <span className="text-[10px] text-gray-600">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Appointment Types */}
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Types</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Standard', bg: 'bg-white border border-gray-200' },
                  { label: 'ALF', bg: 'bg-purple-50 border border-purple-200' },
                  { label: 'Event', bg: 'bg-blue-50 border border-blue-200' },
                ].map(t => (
                  <div key={t.label} className="flex items-center gap-1">
                    <span className={`inline-block w-3 h-3 rounded-sm ${t.bg}`} />
                    <span className="text-[10px] text-gray-600">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Days Off */}
            <div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Availability</div>
              <div className="flex items-center gap-1">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{
                    backgroundColor: '#fee2e2',
                    border: '1px dashed #fca5a5',
                  }}
                />
                <span className="text-[10px] text-gray-600">Day Off / Unavailable</span>
              </div>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}

export default CalendarPage;
